"use client";

import {
  LegalDocumentPage,
  LegalTextSection,
} from "@/components/legal/LegalDocumentPage";
import { useLanguage } from "@/context/LanguageContext";

export default function Gdpr() {
  const { language } = useLanguage();
  const copy = {
    cs: {
      title: "Zpracování osobních údajů",
      subtitle:
        "platné pro službu Hlídací pes na webu softiq.cz/hlidaci-pes od 2.1.2025",
      sections: [
        {
          title: "Kdo zpracovává Vaše osobní údaje?",
          paragraphs: [
            "Osobní údaje sami zadáváte na webové stránky www.softiq.cz. Provozovatelem webu je Ing. Martin Švejda, IČO 21086231, sídlem Janského 559/13, 779 00, Olomouc – Povel (dále jen „správce“), který je zároveň správcem osobních údajů klientů ve smyslu čl. 7 nařízení Evropského parlamentu a Rady č. 2016/679 (obecné nařízení o ochraně osobních údajů, dále jen „nařízení“).",
          ],
        },
        {
          title: "Jaké osobní údaje jsou zpracovávány?",
          paragraphs: [
            "Abychom Vám mohli poskytnout Vámi požadovanou službu, potřebujeme znát alespoň Váš e-mail, na němž Vás můžeme kontaktovat. Údaje poskytujete výlučně Vy vyplněním příslušného formuláře na webu. Údaje z jiných zdrojů nejsou získávány. Dále zpracováváme adresu, ič, název subjektu, pokud tyto informace klient poskytne.",
          ],
        },
        {
          title: "Právní základ zpracování",
          paragraphs: [
            "Správce je oprávněn zpracovávat Vaše osobní údaje pouze pro účely, pro něž má příslušný právní základ pro zpracování. Vzhledem k činnosti, které správce provozuje, a účelům, pro které správce osobní údaje klientů zpracovává, správce zpracovává osobní údaje na těchto právních základech:",
            "Primárně pro účely splnění smlouvy – splnění služby, kterou si přejete poskytnout, nebo pro provedení opatření přijatých před uzavřením smlouvy na žádost klienta.\nJe-li to nezbytné pro splnění právní povinnosti správce.\nPro ochranu práv a právem chráněných zájmů správce.\nNa základě Vašeho souhlasu.",
          ],
        },
        {
          title: "Účely zpracování",
          paragraphs: [
            "Vaše osobní údaje jsou zpracovány zejména za účelem plnění objednané služby. Správce dále zpracovává osobní údaje klienta za účelem vedení účetní a daňové agendy, plnění zákonných povinností správce dle příslušných daňových a účetních předpisů. Správce je případně oprávněn zpracovávat osobní údaje klienta za účelem určení, výkonu či obhajoby svých právních nároků včetně vymáhání pohledávek za klientem. Vaše osobní údaje mohou být zpracovány též z důvodu zdokonalení služby, případně i pro nabídku dalších služeb.",
          ],
        },
        {
          title: "Příjemci osobních údajů",
          paragraphs: [
            "Správce neposkytuje osobní údaje jiným subjektům.\nSprávce nepředává osobní údaje do třetí země ani mezinárodní organizaci.",
          ],
        },
        {
          title: "Jaká máte v souvislosti se zpracováním osobních údajů práva?",
          paragraphs: [
            "Právo na přístup k osobním údajům: Klient má právo získat od správce potvrzení o tom, zda zpracovává osobní údaje klienta, a pokud ano, má právo na přístup k těmto osobním údajům a informacím o tom, jakým způsobem jsou osobní údaje zpracovávány.",
            "Právo na opravu osobních údajů: Klient má právo na to, aby správce bez zbytečného odkladu opravil osobní údaje, které se klienta týkají, a aby na základě prohlášení klienta doplnil údaje neúplné.",
            "Právo na výmaz osobních údajů: Klient má právo na to, aby správce bez zbytečného odkladu vymazal osobní údaje, které se klienta týkají, pokud:\njiž nejsou potřebné pro účely, pro které byly shromážděny nebo jinak zpracovány;\nklient odvolá souhlas a neexistuje žádný další důvod pro zpracování;\nklient vznese námitky proti zpracování a neexistují žádné převažující oprávněné důvody pro zpracování.",
            "Právo na omezení zpracování: Klient má právo, aby správce omezil zpracování osobních údajů, nejsou-li dořešeny sporné otázky.",
            "Právo na přenositelnost údajů: Klient má právo získat osobní údaje, které poskytl správci na základě souhlasu nebo pro účely plnění smlouvy, ve strukturovaném formátu.",
            "Právo vznést námitku: Klient má právo vznést námitku proti zpracování osobních údajů na základě oprávněného zájmu správce.",
          ],
        },
        {
          title: "Dobrovolnost poskytnutí osobních údajů",
          paragraphs: [
            "Poskytnutí osobních údajů je vždy dobrovolné. V případě, že klient osobní údaje neposkytne pro účely zpracování, nebude možné, aby správce poskytl klientovi relevantní služby.",
          ],
        },
        {
          title: "Jak dlouho jsou osobní údaje zpracovávány?",
          paragraphs: [
            "Osobní údaje vztahující se k uzavření smlouvy a plnění smlouvy správce zpracovává po dobu trvání smlouvy – poskytování služby, a dále pak po dobu nezbytně nutnou pro případ, že by správce musel předkládat důkazy v soudním nebo správním řízení.",
          ],
        },
      ] as LegalTextSection[],
    },
    en: {
      title: "Personal Data Processing",
      subtitle:
        "valid for the Watchdog service at softiq.cz/hlidaci-pes from 2 January 2025",
      sections: [
        {
          title: "Who processes your personal data?",
          paragraphs: [
            "You enter personal data yourself on the website www.softiq.cz. The website operator is Ing. Martin Švejda, Company ID 21086231, with registered office at Janského 559/13, 779 00, Olomouc – Povel (hereinafter the “controller”), who is also the controller of clients’ personal data within the meaning of Article 7 of Regulation (EU) 2016/679 of the European Parliament and of the Council (General Data Protection Regulation, hereinafter the “Regulation”).",
          ],
        },
        {
          title: "What personal data is processed?",
          paragraphs: [
            "In order to provide the service you request, we need to know at least your e-mail address, through which we can contact you. You provide the data exclusively by completing the relevant form on the website. Data from other sources is not obtained. We also process the address, company ID, and business name if the client provides this information.",
          ],
        },
        {
          title: "Legal Basis for Processing",
          paragraphs: [
            "The controller is entitled to process your personal data only for purposes for which there is an appropriate legal basis. Given the activities carried out by the controller and the purposes for which the controller processes clients’ personal data, the controller processes personal data on the following legal bases:",
            "Primarily for the performance of a contract – performance of the service you wish to receive, or for steps taken before entering into a contract at the client’s request.\nWhere necessary to comply with the controller’s legal obligation.\nFor the protection of the controller’s rights and legally protected interests.\nOn the basis of your consent.",
          ],
        },
        {
          title: "Purposes of Processing",
          paragraphs: [
            "Your personal data is processed mainly for the purpose of fulfilling the ordered service. The controller also processes the client’s personal data for accounting and tax records, and to fulfil the controller’s legal obligations under applicable tax and accounting regulations. The controller may also process the client’s personal data for the establishment, exercise, or defence of legal claims, including the recovery of receivables from the client. Your personal data may also be processed to improve the service, or to offer additional services.",
          ],
        },
        {
          title: "Recipients of Personal Data",
          paragraphs: [
            "The controller does not provide personal data to other entities.\nThe controller does not transfer personal data to a third country or international organisation.",
          ],
        },
        {
          title:
            "What rights do you have in connection with personal data processing?",
          paragraphs: [
            "Right of access to personal data: The client has the right to obtain confirmation from the controller as to whether the controller processes the client’s personal data and, if so, has the right of access to that personal data and to information about how the personal data is processed.",
            "Right to rectification of personal data: The client has the right to have the controller correct without undue delay personal data concerning the client and, based on the client’s statement, complete incomplete data.",
            "Right to erasure of personal data: The client has the right to have the controller erase without undue delay personal data concerning the client if:\nthe data is no longer necessary for the purposes for which it was collected or otherwise processed;\nthe client withdraws consent and there is no other reason for processing;\nthe client objects to processing and there are no overriding legitimate grounds for processing.",
            "Right to restriction of processing: The client has the right to have the controller restrict processing of personal data while disputed matters remain unresolved.",
            "Right to data portability: The client has the right to obtain personal data provided to the controller on the basis of consent or for the performance of a contract in a structured format.",
            "Right to object: The client has the right to object to processing of personal data based on the controller’s legitimate interest.",
          ],
        },
        {
          title: "Voluntary Provision of Personal Data",
          paragraphs: [
            "Providing personal data is always voluntary. If the client does not provide personal data for processing purposes, the controller will not be able to provide relevant services to the client.",
          ],
        },
        {
          title: "How long is personal data processed?",
          paragraphs: [
            "The controller processes personal data relating to the conclusion and performance of a contract for the duration of the contract – provision of the service – and then for the time necessary in case the controller must submit evidence in court or administrative proceedings.",
          ],
        },
      ] as LegalTextSection[],
    },
  }[language];

  return (
    <LegalDocumentPage
      title={copy.title}
      subtitle={copy.subtitle}
      sections={copy.sections}
    />
  );
}
